import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { get } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen, EssIcon } from "../../../components/admin/EssUi";
import { CalendarDays, PartyPopper, Package, Upload } from "lucide-react";

const ROWS_PER_PAGE = 10;

const mapStageToKey = (stageName) => {
  const map = {
    "Initial Qualification": "new",
    "First Interview": "screening",
    "Second Interview": "interview",
    "Contract Proposal": "offer",
    "Contract Signed": "hired",
  };
  return map[stageName] || "new";
};

const ESSAdminRecruitmentPage = () => {
  const intl = useIntl();
  const { permissions } = useSelector((state) => state.auth) || {};
  const canManage = !!permissions?.canManageRecruitment;
  const [jobs, setJobs] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [stages, setStages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("pipeline");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      try {
        const [jobsRes, stagesRes, applicantsRes, departmentsRes] = await Promise.all([
          get("/api/jobs"),
          get("/api/recruitment-stages"),
          get("/api/applicants"),
          get("/api/departments"),
        ]);

        const apiJobs = jobsRes?.data || [];
        const apiStages = stagesRes?.data || [];
        const apiApplicants = applicantsRes?.data || [];
        const apiDepts = departmentsRes?.data || [];

        const deptMap = {};
        apiDepts.forEach((d) => { deptMap[d.id] = d.complete_name || d.name; });

        const jobMap = {};
        apiJobs.forEach((j) => { jobMap[j.id] = j.name; });

        const stageMap = {};
        apiStages.forEach((s) => { stageMap[s.id] = s.name; });

        const mappedJobs = apiJobs.map((j) => ({
          ...j,
          department_name: deptMap[j.department_id] || "—",
        }));

        const mappedApplicants = apiApplicants.map((a) => {
          const jobName = jobMap[a.job_id] || "—";
          const stageName = stageMap[a.stage_id] || "New";
          return {
            id: a.id,
            name: a.partner_name || a.name || a.email || a.email_from || "—",
            role: jobName,
            stage: stageName,
            stageId: a.stage_id || 0,
            email: a.email || a.email_from || "—",
            applied: a.create_date
              ? new Date(a.create_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
              : "—",
            priority: a.priority || 0,
          };
        });

        setJobs(mappedJobs);
        setStages(apiStages);
        setApplicants(mappedApplicants);
      } catch {}
      setLoaded(true);
    };
    load();
  }, []);

  if (!loaded) return <LoadingScreen />;

  const hasStages = stages.length > 0;
  const hasApplicants = applicants.length > 0;

  const kanbanColumns = hasStages
    ? stages.map((s) => ({
        key: mapStageToKey(s.name),
        label: s.name,
        count: applicants.filter((a) => a.stage === s.name || a.stageId === s.id).length,
        color: "#f8fafc",
        textColor: s.hired_stage ? "#166534" : "#1e40af",
        bgColor: s.hired_stage ? "#f0fdf4" : "#f8fafc",
      }))
    : [];

  const kanbanCandidates = {};
  if (hasApplicants) {
    stages.forEach((s) => {
      const key = mapStageToKey(s.name);
      kanbanCandidates[key] = applicants
        .filter((a) => a.stage === s.name || a.stageId === s.id)
        .map((a) => ({
          name: a.name,
          role: a.role,
          initials: a.name.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase(),
          bg: "#4f46e5",
        }));
    });
  }

  const candidateTable = applicants.map((a) => ({
    candidate: a.name,
    position: a.role,
    department: "—",
    applied: a.applied,
    score: "—",
    stage: a.stage,
    interviewer: "—",
    status: a.stage,
  }));

  const openJobsCount = jobs.filter((j) => j.state === "recruit" || j.state === "open").length;
  const totalApplications = applicants.length;
  const interviewsCount = applicants.filter((a) => a.stage?.toLowerCase().includes("interview")).length;
  const hiredCount = applicants.filter((a) => a.stage === "Contract Signed" || a.stage === "Hired").length;

  const tabs = [
    { key: "pipeline", label: "Pipeline" },
    { key: "openings", label: "Job Openings", count: openJobsCount },
    { key: "applications", label: "All Applications", count: totalApplications },
    { key: "interviews", label: "Interviews", count: interviewsCount },
    { key: "hired", label: "Hired", count: hiredCount },
    { key: "archive", label: "Archive", count: 0 },
  ];

  const filteredTable = candidateTable;
  const totalPages = Math.max(1, Math.ceil(filteredTable.length / ROWS_PER_PAGE));
  const paginatedTable = filteredTable.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const getStatusBg = (status) => {
    const colors = { "Shortlisted": "#dcfce7", "Offer Sent": "#eef2ff", "Interview": "#fef3c7", "Screening": "#f3e8ff", "Hired": "#cffafe", "Rejected": "#fecdd3" };
    return colors[status] || "#f1f5f9";
  };

  const getStatusColor = (status) => {
    const colors = { "Shortlisted": "#16a34a", "Offer Sent": "#4f46e5", "Interview": "#d97706", "Screening": "#7c3aed", "Hired": "#0891b2", "Rejected": "#e11d48" };
    return colors[status] || "#64748b";
  };

  const getStageColor = (stage) => {
    const colors = { "New": "#4f46e5", "Screening": "#7c3aed", "Interview": "#d97706", "Offer Sent": "#16a34a", "Hired": "#0891b2" };
    return colors[stage] || "#64748b";
  };

  const getStageBg = (stage) => {
    const colors = { "New": "#eef2ff", "Screening": "#f3e8ff", "Interview": "#fef3c7", "Offer Sent": "#dcfce7", "Hired": "#cffafe" };
    return colors[stage] || "#f1f5f9";
  };

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: '0 16px' }}>
      <PageHeader
        title="Recruitment"
        subtitle="Manage job openings, applications and hiring pipeline"
        actions={
          <div style={{ display: 'flex', gap: '12px' }}>
            {canManage && (
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm" style={{ background: "#059669", color: "white", border: 'none', cursor: 'pointer' }}>
              ＋ New Job
            </button>
            )}
            {canManage && (
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all" style={{ background: "#f1f5f9", color: "#334155", border: 'none', cursor: 'pointer' }}>
              <Upload size={16} />
              Publish
            </button>
            )}
          </div>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: '24px' }}>
        <StatCard label="Open Positions" value={openJobsCount} icon="🎯" color="teal" />
        <StatCard label="Applications" value={totalApplications} icon="👥" color="indigo" trend="Across all positions" />
        <StatCard label="Interviews" value={interviewsCount} icon="📅" color="blue" trend="Scheduled" />
        <StatCard label="Offers / Hired" value={hiredCount} icon="📨" color="green" trend="Accepted" />
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 20px', border: 'none', background: activeTab === tab.key ? '#059669' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#475569', borderRadius: '8px', fontSize: '13px',
              fontWeight: activeTab === tab.key ? '600' : '500', cursor: 'pointer', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ background: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : '#f1f5f9', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "pipeline" && (
        <>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Recruitment Pipeline</h3>
            </div>
            <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
              {kanbanColumns.map((col) => (
                <div key={col.key} style={{ flexShrink: 0, width: '220px' }}>
                  <div style={{ padding: '10px 14px', borderRadius: '8px 8px 0 0', background: col.color, color: col.textColor, fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.05em', display: 'flex', justifyContent: 'space-between', borderBottom: `2px solid ${col.textColor || '#1e40af'}` }}>
                    <span>{col.label}</span>
                    <span style={{ background: 'rgba(0,0,0,0.08)', padding: '1px 10px', borderRadius: '12px', fontSize: '11px' }}>
                      {col.count}
                    </span>
                  </div>
                  <div style={{ background: col.bgColor, borderRadius: '0 0 8px 8px', padding: '8px', minHeight: '180px', border: '1px solid #f1f5f9', borderTop: 'none' }}>
                    {(kanbanCandidates[col.key] || []).length === 0 ? (
                      <div style={{ padding: '20px 10px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>No candidates</div>
                    ) : (kanbanCandidates[col.key] || []).map((c, i) => (
                      <div key={i} style={{ background: 'white', borderRadius: '8px', padding: '12px', border: '1px solid #e2e8f0', cursor: 'pointer', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: c.bg || '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: 'white', flexShrink: 0 }}>
                            {c.initials || c.name.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{c.name}</div>
                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{c.role}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Candidate</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Position</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Applied</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stage</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTable.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>No candidates found</td></tr>
                  ) : paginatedTable.map((row, index) => (
                    <tr key={index} style={{ borderBottom: index < paginatedTable.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{row.candidate}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569' }}>{row.position}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>{row.applied}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: getStageBg(row.stage), color: getStageColor(row.stage), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' }}>{row.stage}</span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: getStatusBg(row.status), color: getStatusColor(row.status), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>{row.status}</span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <button style={{ border: 'none', background: 'transparent', color: '#4f46e5', fontSize: '13px', fontWeight: '500', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                          onMouseEnter={(e) => e.target.style.background = '#eef2ff'}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '16px', borderTop: '1px solid #e2e8f0' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: page === 1 ? '#f8fafc' : 'white', color: page === 1 ? '#94a3b8' : '#059669', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
                >← Previous</button>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: page === totalPages ? '#f8fafc' : 'white', color: page === totalPages ? '#94a3b8' : '#059669', cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
                >Next →</button>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "openings" && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Job Title</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Department</th>
                  <th style={{ textAlign: 'center', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recruitment</th>
                  <th style={{ textAlign: 'center', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hired</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>No job openings found</td></tr>
                ) : jobs.map((job, index) => (
                  <tr key={job.id} style={{ borderBottom: index < jobs.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{job.name}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b' }}>{job.department_name || "—"}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: '#1e293b', textAlign: 'center' }}>{job.no_of_recruitment || 0}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: '#16a34a', textAlign: 'center' }}>{job.no_of_hired_employee || 0}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ background: job.state === "recruit" ? '#dcfce7' : '#f1f5f9', color: job.state === "recruit" ? '#16a34a' : '#94a3b8', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                        {job.state === "recruit" ? "Recruiting" : job.state === "open" ? "Open" : job.state || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "applications" && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Candidate</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Position</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Applied</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stage</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
                </tr>
              </thead>
              <tbody>
                {candidateTable.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>No applications found</td></tr>
                ) : candidateTable.map((row, index) => (
                  <tr key={index} style={{ borderBottom: index < candidateTable.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{row.candidate}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569' }}>{row.position}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>{row.applied}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ background: getStageBg(row.stage), color: getStageColor(row.stage), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' }}>{row.stage}</span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ background: getStatusBg(row.status), color: getStatusColor(row.status), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>{row.status}</span>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <button style={{ border: 'none', background: 'transparent', color: '#4f46e5', fontSize: '13px', fontWeight: '500', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                        onMouseEnter={(e) => e.target.style.background = '#eef2ff'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "interviews" && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px 20px', textAlign: 'center' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}><CalendarDays size={48} /></span>
          <p style={{ fontSize: '16px', fontWeight: '500', color: '#64748b' }}>{interviewsCount} interview(s) scheduled</p>
          <p style={{ fontSize: '14px', color: '#94a3b8', marginTop: '4px' }}>Calendar view coming soon</p>
        </div>
      )}

      {activeTab === "hired" && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px 20px', textAlign: 'center' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}><PartyPopper size={48} /></span>
          <p style={{ fontSize: '16px', fontWeight: '500', color: '#64748b' }}>{hiredCount} candidate(s) hired</p>
          <p style={{ fontSize: '14px', color: '#94a3b8', marginTop: '4px' }}>Welcome to the team!</p>
        </div>
      )}

      {activeTab === "archive" && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px 20px', textAlign: 'center' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}><Package size={48} /></span>
          <p style={{ fontSize: '16px', fontWeight: '500', color: '#64748b' }}>No archived applications</p>
          <p style={{ fontSize: '14px', color: '#94a3b8', marginTop: '4px' }}>Archive candidates here</p>
        </div>
      )}
    </div>
  );
};

export default ESSAdminRecruitmentPage;
